import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
} from "antd";
import { create, getById, update } from "../../../services/plans.service";
import { frequencies } from "../../../Enums/Frequency.enums";
import { AlertService } from "../../../services/alert.service";
import { getAll as loadServices } from "../../../services/services.service";
import ConfirmModal from "../../../components/Commons/Modal/confirm-modal.component";
import { formatCurrency } from "../../../services/utils";

const { TextArea } = Input;

export default function FormPlan(props: any) {
  const [services, setServices] = useState<any>([]);
  const [valuesChanged, setValuesChanged] = useState<boolean>(false);
  const [leaveThePage, setLeaveThePage] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(true);

  const { uuid } = useParams();

  const [formPlan] = Form.useForm();
  const navigate = useNavigate();

  const settings = {
    create: { title: "Criar plano", alertMessage: "gravada" },
    edit: { title: "Editar plano", alertMessage: "editada" },
    duplicate: { title: "Duplicar plano", alertMessage: "duplicada" },
  };

  const goPlansList = () => {
    navigate("/management/plans");
  };

  useQuery(["loadPlan", uuid], () => (uuid ? getById(uuid) : null), {
    onSuccess: (response) => {
      if (response) {
        formPlan.setFieldsValue(response.data);
        setActive(response.data.active);
      }
    },
    refetchOnWindowFocus: false,
  });

  useQuery(["loadServices"], () => loadServices(), {
    onSuccess: (response) => {
      setServices(
        response.data.map((service: any) => {
          return {
            key: service.data.id,
            ...service.data,
          };
        })
      );
    },
    refetchOnWindowFocus: false,
  });

  const { mutate: createPlan, isLoading: createPlanLoading } = useMutation(
    create,
    {
      onSuccess: () => {
        AlertService.sendAlert([
          {
            text: "O plano foi cadastrado com sucesso.",
            nextPage: true,
          },
        ]);

        goPlansList();
      },
    }
  );

  const { mutate: updatePlan, isLoading: updatePlanLoading } = useMutation(
    update,
    {
      onSuccess: () => {
        AlertService.sendAlert([
          {
            text: "O plano foi atualizado com sucesso.",
            nextPage: true,
          },
        ]);

        goPlansList();
      },
    }
  );

  const onSubmit = () => {
    formPlan
      .validateFields()
      .then((values) => {
        if (!uuid || props.action == "duplicate") {
          createPlan(values);
        } else {
          updatePlan({ values, uuid });
        }
      })
      .catch(() =>
        AlertService.sendAlert([
          { text: "Campo(s) com preenchimento inválido", type: "error" },
        ])
      );
  };

  const onChangeActive = (value: boolean) => {
    setActive(value);
  };

  const onUpdatePrice = () => {
    let selected = formPlan.getFieldValue("services");

    let price = services.filter((service: any) => selected.includes(service.id))
      .reduce((total: number, service: any) => total += service.value, 0);

    formPlan.setFieldValue("price", price);
  };

  return (
    <>
      <Card
        color="primary"
        styles={{
          header: {
            color: "#0899d1",
            fontWeight: 600,
          },
        }}
        title={settings[props.action as keyof typeof settings].title}
      >
        <Form
          form={formPlan}
          name="form_plan"
          layout="vertical"
          className="form_plan"
          autoComplete="off"
          onValuesChange={() => setValuesChanged(true)}
        >
          <Row gutter={20}>
            <Col
              lg={{ span: 12 }}
              md={{ span: 12 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Plano"
                name="name"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Input
                  placeholder="Nome do plano"
                  size="large"
                  maxLength={100}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label="Descrição"
                name="description"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <TextArea size="large" showCount maxLength={255} />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 8 }}
              md={{ span: 12 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Form.Item
                label="Frequência"
                name="frequency"
                rules={[{ required: true, message: "Campo obrigatório" }]}
              >
                <Select
                  showSearch
                  placeholder="Informe a frequência"
                  size="large"
                  options={frequencies}
                ></Select>
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 3 }}
              md={{ span: 6 }}
              sm={{ span: 12 }}
              xs={{ span: 24 }}
            >
              <Form.Item label="Preço" name="price">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  size="large"
                  maxLength={5}
                  formatter={(value: any) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                  }
                />
              </Form.Item>
            </Col>

            <Col
              lg={{ span: 3 }}
              md={{ span: 6 }}
              sm={{ span: 12 }}
              xs={{ span: 24 }}
            >
              <Form.Item label="Desconto" name="discount">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  size="large"
                  maxLength={5}
                  formatter={(value: any) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col span={24}>
              <Form.Item
                label="Serviços"
                name="services"
                className="item_filter"
              >
                <Select
                  placeholder="Selecione os serviços"
                  size="large"
                  style={{ width: "100%" }}
                  maxTagCount="responsive"
                  mode="multiple"
                  onChange={onUpdatePrice}
                >
                  {services?.map((service: any) => (
                    <Select.Option key={service.id} value={service.id} disabled={!service.active}>
                      {service.name} ({formatCurrency(service.value)})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col>
              {uuid && (
                <Form.Item
                  label={`Plano ${active ? "ativo" : "não ativo"}`}
                  name="active"
                >
                  <Switch checked={active} onChange={onChangeActive} />
                </Form.Item>
              )}
            </Col>
          </Row>
        </Form>

        <Row
          style={{
            marginTop: 20,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Col>
            <Space>
              <Button
                loading={createPlanLoading || updatePlanLoading}
                size="large"
                onClick={() => {
                  if (props.action != "edit" || valuesChanged) {
                    setLeaveThePage(true);
                  } else {
                    goPlansList();
                  }
                }}
              >
                Cancelar
              </Button>

              <Button
                type="primary"
                loading={createPlanLoading || updatePlanLoading}
                size="large"
                onClick={onSubmit}
              >
                Gravar
              </Button>
            </Space>
          </Col>
        </Row>

        <ConfirmModal
          open={leaveThePage}
          title="Tem certeza de que deseja sair sem gravar as alterações?"
          content="Toda a informação não gravada será perdida."
          okText="Sair"
          cancelText="Voltar"
          onOk={() => {
            setLeaveThePage(false);
            goPlansList();
          }}
          onCancel={() => setLeaveThePage(false)}
        ></ConfirmModal>
      </Card>
    </>
  );
}
